import { useEffect, useMemo, useRef, useState } from 'react';

import { UserGroup03Icon } from '@hugeicons/core-free-icons';
import cn from 'classnames';
import { CandidateCheckScreens } from 'constants/candidate_screens';
import { buildNavPath as buildNavPathHelper } from 'helpers/candidate_application/form-page-helpers';
import { useQueryParams } from 'hooks/query-params';
import { useCandidateApplicationUploadActions } from 'hooks/upload-actions';
import { useUserAgent } from 'hooks/user-agent';
import CandidatePage from 'layouts/CandidatePage/CandidatePage';
import numberToWords from 'number-to-words';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { shouldSubmitCandidate } from 'utils/checks-utils';
import { CustomButton } from 'views/CandidateApp/components/CustomButton/CustomButton';

import { Form } from 'components/Form/Form/Form';
import { IconComponent } from 'components/Icon/IconComponent';
import { OrderNumber } from 'components/OrderNumber/OrderNumber';

import {
  createReplacementReferee,
  saveCustomReference,
  selectCandidateApplication,
  submitCandidateApplication,
} from 'api/candidate-application-slice';

import styles from './CandidateCustomReference.module.scss';

const CandidateCustomReference = () => {
  const navigate = useNavigate();
  const [admin] = useQueryParams(['admin']);

  // Helper to build navigation path with admin parameter preserved
  const buildNavPath = path => buildNavPathHelper(path, admin);
  const dispatch = useDispatch();

  const { user_agent } = useUserAgent();
  const { token, replacement } = useParams();
  const { t } = useTranslation();

  const {
    application: {
      candidate,
      brand,
      candidate_checks,
      check_types,
      forms,
      country,
      referees,
      check_values,
    },
    fetched,
  } = useSelector(selectCandidateApplication);

  const { getUpload, signUpload, createUpload, deleteUpload } =
    useCandidateApplicationUploadActions(token);

  const [loading, setLoading] = useState(false);
  const [referenceCheck, setReferenceCheck] = useState(null);
  const [pendingReferees, setPendingReferees] = useState([]);
  const [refereeEmails, setRefereeEmails] = useState([]);
  const [refereeContacts, setRefereeContacts] = useState([]);
  const [showIntro, setShowIntro] = useState(true);

  const formRef = useRef(null);

  // Data
  const [currentReferee, setCurrentReferee] = useState(0);
  const [currentForm, setCurrentForm] = useState(null);

  // Set check
  useEffect(() => {
    if (!fetched) return;

    const referenceCheck = candidate_checks.find(check => check.type === 'custom_reference');
    setReferenceCheck(referenceCheck);

    if (replacement) {
      const refereeToReplace = (referees || [])?.find(r => r.id == replacement);
      const replacementReferee = (referees || [])?.find(r => r.id == refereeToReplace?.replaced_by);

      if (!replacementReferee) {
        setLoading(true);
        dispatch(
          createReplacementReferee({
            token,
            id: referenceCheck.id,
            refereeId: refereeToReplace.id,
          }),
        ).then(({ payload: replacementReferee }) => {
          setPendingReferees([replacementReferee]);
          setLoading(false);
        });
      } else {
        setPendingReferees([replacementReferee]);
      }
    } else {
      const pendingReferees = (referees || [])?.filter(
        referee => !referee?.replaced_by && !referee?.submitted_at && !referee?.canceled,
      );
      setPendingReferees(pendingReferees);
    }
  }, [fetched]);

  // Set current form
  useEffect(() => {
    if (pendingReferees?.length < 1) return;

    const referee = pendingReferees[currentReferee];
    setCurrentForm((forms || []).find(form => form?.id === referee?.candidate_form_id));
  }, [currentReferee, forms, pendingReferees.length]);

  // Reset form
  useEffect(() => {
    if (!currentForm) return;

    formRef.current?.reset();
  }, [currentForm]);

  useEffect(() => {
    if (showIntro) {
      setRefereeEmails([]);
      setRefereeContacts([]);
    }
  }, [showIntro]);

  const candidateEmail = useMemo(() => {
    const emails = [];
    if (candidate?.email) emails.push(candidate.email);
    if (check_values?.values?.email) emails.push(check_values.values.email);
    return emails;
  }, [check_values, candidate]);

  const candidateContact = useMemo(() => {
    const contact = [];
    if (candidate?.contact_number) contact.push(candidate.contact_number);
    if (check_values?.values?.phone_number) contact.push(check_values.values.phone_number);
    return contact;
  }, [check_values, candidate]);

  const handleStartForm = () => {
    setShowIntro(false);
  };

  const onSubmit = async (answers, dataWithLabels) => {
    const refereeData = Object.values(dataWithLabels || {});
    const email = (refereeData || [])?.find(label => label.locked && label.type === 'email')?.value;
    const contact = (refereeData || [])?.find(
      label => label.locked && label.type === 'phone_number',
    )?.value;
    if (email) {
      setRefereeEmails(prev => (prev?.includes(email) ? prev : [...prev, email]));
    }
    if (contact) {
      setRefereeContacts(prev => (prev?.includes(contact) ? prev : [...prev, contact]));
    }

    const _pendingReferees = (pendingReferees || [])?.map((referee, i) => {
      if (i !== currentReferee) return referee;

      return {
        ...referee,
        answers,
        ...(replacement && { replacement }),
      };
    });

    if (currentReferee < _pendingReferees?.length - 1) {
      formRef.current.reset();
      setPendingReferees(_pendingReferees);
      setCurrentReferee(index => index + 1);
    } else {
      setLoading(true);
      await dispatch(
        saveCustomReference({
          token,
          id: referenceCheck.id,
          referees: _pendingReferees,
          user_agent,
        }),
      );
      if (shouldSubmitCandidate(referenceCheck, candidate_checks, check_types)) {
        await dispatch(submitCandidateApplication({ token, user_agent }));
        setLoading(false);
        navigate(
          buildNavPath(`/form_submission/candidate/${CandidateCheckScreens.SUCCESS}/${token}`),
        );
      } else {
        setLoading(false);
        navigate(buildNavPath(`/form_submission/candidate/dashboard/${token}`));
      }
    }
  };

  return (
    <CandidatePage
      loading={loading}
      token={token}
      withTopLogo
      brand={brand}
      replacement={replacement}>
      <CandidatePage.View>
        {referenceCheck && (
          <CandidatePage.Card className={styles.root}>
            {showIntro ? (
              // Intro Screen
              <div className="d-flex flex-column align-items-start u-gap-large u-margin-bottom--large u-width-100">
                <IconComponent icon={UserGroup03Icon} size="xxlarge" />
                <div className="d-flex flex-column align-items-start u-gap">
                  <h1 className="primary-text u-font-weight-500 u-margin-0">
                    {t('common.yourReferees')}
                  </h1>
                  {replacement && (
                    <p className="secondary-text u-font-size-medium u-font-weight-400">
                      {t('candidate.customReference.backupReferee')}
                    </p>
                  )}
                  {!replacement && (
                    <span className="secondary-text u-font-size-medium u-font-weight-400 u-sentence-case">
                      You need to nominate {pendingReferees.length}{' '}
                      {pendingReferees.length === 1 ? t('common.referee') : t('common.referees')} to
                      complete your {t('referee.referenceCheck')}.
                    </span>
                  )}
                </div>
                <CustomButton
                  className={cn(styles.startButton, 'button button--primary')}
                  brand={brand}
                  onClick={handleStartForm}>
                  Get Started
                </CustomButton>
              </div>
            ) : (
              // Form Screen
              <div className="d-flex flex-column align-items-start u-gap-large u-margin-bottom--large u-width-100">
                <div className="d-flex flex-column u-gap-medium">
                  <OrderNumber orderNumber={currentReferee + 1} />
                  <span className="primary-text u-font-size-xx-large u-font-weight-500 u-margin-0 u-sentence-case">
                    {replacement
                      ? 'Nominate your backup' + t('common.referee')
                      : `Nominate your ${numberToWords.toWordsOrdinal(currentReferee + 1)} ${t('common.referee')}`}
                  </span>
                </div>
                {currentForm?.description && (
                  <p className="secondary-text u-font-size-medium u-font-weight-400">
                    {currentForm.description}
                  </p>
                )}
                <div className="u-width-100">
                  <Form
                    candidateId={candidate.id}
                    country={country}
                    ref={formRef}
                    textParams={{
                      candidate: candidate.first_name,
                      candidateFirstName: candidate.first_name,
                      candidateFullName: candidate.name,
                      candidateLastName: candidate.last_name,
                      job: candidate.job_role,
                    }}
                    form={currentForm}
                    brand={brand}
                    buttonTitle={pendingReferees.length === currentReferee + 1 ? 'Finish' : 'Next'}
                    onSubmit={onSubmit}
                    getCandidateUpload={getUpload}
                    deleteCandidateUpload={deleteUpload}
                    signCandidateUpload={signUpload}
                    createCandidateUpload={createUpload}
                    includeCandidateReferenceValidation
                    candidateEmail={[...candidateEmail, ...refereeEmails]}
                    candidateContact={[...candidateContact, ...refereeContacts]}
                    emailCompareErrorMessage={t(
                      'candidate.customReference.emailCompareErrorMessage',
                    )}
                    contactCompareErrorMessage={t(
                      'candidate.customReference.contactCompareErrorMessage',
                    )}
                  />
                </div>
              </div>
            )}
          </CandidatePage.Card>
        )}
      </CandidatePage.View>
    </CandidatePage>
  );
};

export default CandidateCustomReference;
